import './App.less';
import { useEffect, useMemo, useState, memo, Suspense, lazy } from 'react';
import PropTypes from 'prop-types';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import dayjs from 'dayjs';
import en from 'dayjs/locale/en';
import { notification } from 'antd';
import Loader from '../components/common/Loader.jsx';
import Header from '../components/Header.jsx';
import PublicRoute from '../components/PublicRoute.jsx';
import ErrorBoundaryWrapper from '../hoc/ErrorBoundary.jsx';
import { PermissionsContext } from '../hoc/withPermissions.jsx';

const SettingsWrapperPageContainer = lazy(() => import('../containers/SettingsWrapperPageContainer.js'));
const DashboardPageContainer = lazy(() => import('../containers/DashboardPageContainer.js'));
const TimesheetPageContainer = lazy(() => import('../containers/TimesheetPageContainer.js'));
const UserProfileContainer = lazy(() => import('../containers/UserProfileContainer.js'));
const ManagementPageContainer = lazy(() => import('../containers/ManagementPageContainer.js'));
const TeamsPageContainer = lazy(() => import('../containers/TeamsPageContainer.js'));
const CalendarPage = lazy(() => import('../pages/CalendarPage.jsx'));
const NotFoundPage = lazy(() => import('../pages/NotFoundPage.jsx'));
const PrivacyPolicyPage = lazy(() => import('../pages/PrivacyPolicyPage.jsx'));
const DebugPageContainer = lazy(() => import('../containers/DebugPageContainer.js'));
const CreateNewPasswordPageContainer = lazy(() => import('../containers/CreateNewPasswordPageContainer.jsx'));
const ResetPasswordPageContainer = lazy(() => import('../containers/ResetPasswordPageContainer.js'));
const LoginPageContainer = lazy(() => import('../containers/LoginPageContainer.js'));
const OvertimesStatisticsContainer = lazy(() => import('../containers/management/OvertimesStatisticsContainer.js'));
const VacationsContainer = lazy(() => import('../containers/management/VacationsContainer.js'));
const HolidaysContainer = lazy(() => import('../containers/management/HolidaysContainer.js'));
const SickLeaveStatisticsContainer = lazy(() => import('../containers/management/SickLeaveStatisticsContainer.js'));
const WorkingTimeStatisticsContainer = lazy(() => import('../containers/management/WorkingTimeStatisticsContainer.js'));
const CalendarContainer = lazy(() => import('../containers/teams/CalendarContainer.js'));
const TeamContainer = lazy(() => import('../containers/teams/TeamContainer.js'));
const TeamsContainer = lazy(() => import('../containers/teams/TeamsContainer.js'));
const UsersContainer = lazy(() => import('../containers/teams/UsersContainer.js'));
const SettingsPage = lazy(() => import('../containers/SettingsPageContainer.js'));
const EmailsTemplatesPageContainer = lazy(() => import('../containers/EmailsTemplatesPageContainer.js'));

dayjs.locale({
    ...en,
    weekStart: 1,
    yearStart: 4,
});

notification.config({
    placement: 'bottomRight',
    bottom: 50,
    duration: 4,
});

const publicRouters = [
    {
        path: '/',
        element: <LoginPageContainer />,
    },
    {
        path: '/reset-password/:id?',
        element: <ResetPasswordPageContainer />,
    },
    {
        path: '/newpassword',
        element: <CreateNewPasswordPageContainer />,
    },
    {
        path: '/privacy-policy',
        element: <PrivacyPolicyPage />,
    },
];

const App = ({ userId, isAuthenticated, initialized, permissions, settings, ...rest }) => {
    const [routers, setRouters] = useState([]);

    useEffect(() => {
        setRouters(getRouters(permissions));
    }, [permissions, userId]);

    const contextValue = useMemo(() => ({ permissions, settings }), [permissions, settings]);

    return (
        <Suspense fallback={<Loader />}>
            <PermissionsContext value={contextValue}>
                <Routes>
                    <Route element={<PublicRoute />}>
                        {publicRouters.map(({ path, element }, i) => (
                            <Route key={i} path={path} element={element} />
                        ))}
                    </Route>
                    <Route
                        element={
                            <PrivateRoute
                                {...rest}
                                initialized={initialized}
                                isAuthenticated={isAuthenticated}
                                routers={routers}
                            />
                        }
                    >
                        <Route path="dashboard" element={<DashboardPageContainer />} />
                        <Route path="timesheet" element={<TimesheetPageContainer />} />
                        {permissions.balanceChanger && (
                            <>
                                <Route path="/dashboard" element={<CalendarPage />}>
                                    <Route index element={<DashboardPageContainer />} />
                                    <Route path="timesheet" element={<TimesheetPageContainer />} />
                                </Route>
                                <Route path="/management" element={<ManagementPageContainer />}>
                                    <Route index element={<WorkingTimeStatisticsContainer />} />
                                    <Route path="balance" element={<VacationsContainer />} />
                                    <Route path="overtimes" element={<OvertimesStatisticsContainer />} />
                                    <Route path="sick-leaves" element={<SickLeaveStatisticsContainer />} />
                                    <Route path="holidays" element={<HolidaysContainer />} />
                                </Route>
                                <Route path="/teams" element={<TeamsPageContainer />}>
                                    <Route index element={<TeamsContainer />} />
                                    <Route path="team/:id" element={<TeamContainer />} />
                                    <Route path="users" element={<UsersContainer />} />
                                    <Route path="calendar" element={<CalendarContainer />} />
                                </Route>
                                <Route path="/settings" element={<SettingsWrapperPageContainer />}>
                                    <Route index element={<SettingsPage />} />
                                    <Route path="templates" element={<EmailsTemplatesPageContainer />} />
                                </Route>
                            </>
                        )}
                        <Route path="debug" element={<DebugPageContainer />} />
                        <Route path="profile" element={<UserProfileContainer />} />
                    </Route>
                    <Route path="*" element={<NotFoundPage />} />
                </Routes>
            </PermissionsContext>
        </Suspense>
    );
};

App.propTypes = {
    userId: PropTypes.number.isRequired,
    isAuthenticated: PropTypes.bool.isRequired,
    location: PropTypes.object,
    permissions: PropTypes.object.isRequired,
    loginWithSecretKey: PropTypes.func.isRequired,
    settings: PropTypes.object.isRequired,
};

export default memo(App);

const PrivateRoute = ({ isAuthenticated, initialized, routers, logout, userFirstName, userLastName }) => {
    if (!initialized) {
        return <Loader />;
    }

    return !isAuthenticated ? (
        <Navigate to="/" />
    ) : (
        <>
            <Header logout={logout} routers={routers} userFirstName={userFirstName} userLastName={userLastName} />
            <Suspense fallback={<Loader />}>
                <ErrorBoundaryWrapper>
                    <Outlet />
                </ErrorBoundaryWrapper>
            </Suspense>
        </>
    );
};

PrivateRoute.propTypes = {
    permissions: PropTypes.object,
    isAuthenticated: PropTypes.bool,
    userFirstName: PropTypes.string,
    userLastName: PropTypes.string,
    logout: PropTypes.func,
    routers: PropTypes.array,
};

const getRouters = (permissions) => {
    let defaultRouters = [];

    if (permissions.settings) {
        defaultRouters.push({
            path: '/settings',
            name: 'Settings',
            element: <SettingsWrapperPageContainer />,
        });
    }
    if (permissions.timeManager) {
        defaultRouters.push({
            path: '/debug',
            name: 'Debug',
            element: <DebugPageContainer />,
        });
    }
    defaultRouters.push({
        path: '/profile',
        name: 'My profile',
        element: <UserProfileContainer />,
    });

    if (permissions.customer) {
        return [
            {
                path: '/dashboard',
                name: 'Dashboard',
                element: <DashboardPageContainer />,
            },
            ...defaultRouters,
        ];
    }
    /** exist only permissions (without roles), but view different like for admin and user.
     *  Balance Hanger permission is most important, so for now it's a main criteria
     * */
    if (!permissions.balanceChanger) {
        return [
            {
                path: '/dashboard',
                name: 'Dashboard',
                element: <DashboardPageContainer />,
            },
            {
                path: '/timesheet',
                name: 'Timesheet',
                element: <TimesheetPageContainer />,
            },
            ...defaultRouters,
        ];
    }

    let routers = [
        {
            path: '/dashboard',
            name: 'Calendar',
            element: <CalendarPage />,
        },
    ];

    if (permissions.vacation && permissions.balanceChanger && !permissions.working) {
        return [
            {
                path: '/dashboard',
                name: 'Dashboard',
                element: <DashboardPageContainer />,
            },
            {
                path: '/management/balance',
                name: 'Management',
                element: <VacationsContainer />,
            },
            ...defaultRouters,
        ];
    }

    if (permissions.working || permissions.holidays) {
        routers.push({
            path: '/management',
            name: 'Management',
            element: <ManagementPageContainer />,
        });
    }

    if (permissions.calendar || permissions.user || permissions.team) {
        routers.push({
            path: '/teams',
            name: 'Teams',
            element: <TeamsPageContainer />,
        });
    }

    return [...routers, ...defaultRouters];
};
